import React, { useRef, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { mq } from 'style/variables';
import { CareerDetailHeading } from 'components/unique/careerDetail/careerDetailHeading';
import ScrollGrayCover from 'components/atoms/img/ScrollGrayCover';
import arrowScrollSvg from 'assets/images/career/arrow-scroll.svg';
type CareerDetailIdealCandidateProps = {
  idealCandidate: Array<{
    url: string;
    desc: string;
    url_org: string;
  }>;
};

const IdealCandidateWrapper = styled.div``;

const IdealCandidateContent = styled.div`
  margin-top: 32px;
  position: relative;
  z-index: 1;
`;

const HorizontalScrollContainer = styled.div`
  display: flex;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-behavior: smooth;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  scroll-snap-type: x mandatory;
  scroll-padding: 0;

  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera */
  }
`;

const ScrollableContent = styled.div`
  display: flex;
  gap: 20px;
`;

const IdealCandidateItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  scroll-snap-align: start;
  scroll-snap-stop: always;

  /* On PC: make the second-to-last slide snap to the right edge */
  &:nth-last-of-type(2) {
    scroll-snap-align: end;
  }

  /* On mobile: keep the very last slide snapping to the right edge */
  ${mq.onlysp} {
    &:nth-last-of-type(2) {
      scroll-snap-align: start;
    }
    &:last-of-type {
      scroll-snap-align: end;
    }
  }
`;

const IdealCandidateImage = styled.img`
  width: 393px;
  height: 393px;
  max-width: max-content;
  ${mq.onlysp} {
    width: 278px;
    height: 278px;
  }

  object-fit: cover;
`;

// コントロールエリア（スクロールバー + ナビゲーションボタン）
const ControlArea = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin-top: 20px;

  ${mq.onlysp} {
    display: none;
  }
`;

// スクロールバー
const ScrollBarContainer = styled.div`
  width: calc(393px * 2 + 20px);
  max-width: 100%;
  height: 1px;
  background-color: #d9d9d9;

  ${mq.onlysp} {
    width: 100%;
  }
`;

const ScrollBar = styled.div<{ progress: number }>`
  height: 100%;
  background-color: #191210;
  width: ${props => props.progress}%;
  min-width: 30px;
  transition: width 0.3s ease;
`;

// ナビゲーションボタン
const NavigationContainer = styled.div`
  display: flex;
  gap: 10px;
  margin-right: 30px;
`;

const NavButton = styled.button<{ disabled?: boolean }>`
  width: 32px;
  height: 32px;
  border: none;
  background: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  &:hover:not(:disabled) {
    transform: scale(1.1);
  }

  &:disabled {
    opacity: 0.3;
  }
`;

const ArrowIcon = styled.img<{ isReversed?: boolean }>`
  width: 100%;
  height: 100%;
  transform: ${props => (props.isReversed ? 'scaleX(-1)' : 'none')};
  transition: transform 0.3s ease;
`;

export const CareerDetailIdealCandidate = ({ idealCandidate }: CareerDetailIdealCandidateProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // 横スクロールの状態管理
  const totalItems = idealCandidate?.length || 0;
  const lastIndex = totalItems > 0 ? (isMobile ? totalItems - 1 : Math.max(totalItems - 2, 0)) : 0;
  const isFirstItem = currentIndex === 0;
  const isLastItem = currentIndex === lastIndex;

  // リアルタイムでモバイル判定を更新
  useEffect(() => {
    const updateMobileStatus = () => {
      const screenWidth = window.innerWidth;
      const newIsMobile = screenWidth <= 768;
      setIsMobile(newIsMobile);
    };

    // 初期判定
    updateMobileStatus();

    // リサイズ時にリアルタイム更新
    window.addEventListener('resize', updateMobileStatus);

    return () => {
      window.removeEventListener('resize', updateMobileStatus);
    };
  }, []);

  // レスポンシブ対応のアイテム幅とギャップ（リアルタイム計算）
  const getItemWidth = () => {
    return isMobile ? 278 : 393;
  };

  const getGap = () => {
    return 20; // ギャップは固定
  };

  const getScrollDistance = () => {
    const itemWidth = getItemWidth();
    const gap = getGap();
    return itemWidth + gap;
  };

  // 前のアイテムに移動
  const goToPrevious = () => {
    if (!isFirstItem) {
      const newIndex = currentIndex - 1;
      setCurrentIndex(newIndex);
      const scroller = scrollContainerRef.current;
      if (scroller) {
        const left = newIndex * getScrollDistance();
        scroller.scrollTo({ left, behavior: 'smooth' });
      }
    }
  };

  // 次のアイテムに移動
  const goToNext = () => {
    if (!isLastItem) {
      const newIndex = currentIndex + 1;
      setCurrentIndex(newIndex);
      const scroller = scrollContainerRef.current;
      if (scroller) {
        const distance = getScrollDistance();
        let left = newIndex * distance;
        if (newIndex === lastIndex) {
          left = scroller.scrollWidth - scroller.clientWidth; // snap last slide to right edge
        }
        scroller.scrollTo({ left, behavior: 'smooth' });
      }
    }
  };

  // スクロールバーの進行状況を計算
  const scrollProgress = lastIndex > 0 ? (currentIndex / lastIndex) * 100 : 0;

  return (
    <IdealCandidateWrapper>
      <CareerDetailHeading titleEn="Ideal Candidate" titleJa="求める人物像" />
      {idealCandidate && (
        <IdealCandidateContent>
          <HorizontalScrollContainer ref={scrollContainerRef}>
            <ScrollableContent>
              {idealCandidate.map((candidate, index) => (
                <IdealCandidateItem key={index}>
                  <ScrollGrayCover>
                    <IdealCandidateImage src={candidate.url} alt={candidate.desc || ''} />
                  </ScrollGrayCover>
                </IdealCandidateItem>
              ))}
            </ScrollableContent>
          </HorizontalScrollContainer>

          {/* コントロールエリア（スクロールバー + ナビゲーションボタン） */}
          <ControlArea>
            {/* スクロールバー */}
            <ScrollBarContainer>
              <ScrollBar progress={scrollProgress} />
            </ScrollBarContainer>

            {/* ナビゲーションボタン */}
            <NavigationContainer>
              <NavButton onClick={goToPrevious} disabled={isFirstItem}>
                <ArrowIcon src={arrowScrollSvg} alt="前へ" isReversed={true} />
              </NavButton>
              <NavButton onClick={goToNext} disabled={isLastItem}>
                <ArrowIcon src={arrowScrollSvg} alt="次へ" isReversed={false} />
              </NavButton>
            </NavigationContainer>
          </ControlArea>
        </IdealCandidateContent>
      )}
    </IdealCandidateWrapper>
  );
};
